import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Trophy, X } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

const tierConfig = {
  bronze: {
    gradient: 'from-amber-700 via-amber-600 to-yellow-700',
    border: 'border-amber-500/60',
    glow: 'shadow-[0_0_40px_rgba(217,119,6,0.3)]',
    label: 'Bronze',
    labelColor: 'text-amber-400',
    bgAccent: 'bg-amber-500/10',
  },
  silver: {
    gradient: 'from-gray-400 via-gray-300 to-gray-400',
    border: 'border-gray-300/60',
    glow: 'shadow-[0_0_40px_rgba(192,192,192,0.3)]',
    label: 'Silver',
    labelColor: 'text-gray-300',
    bgAccent: 'bg-gray-400/10',
  },
  gold: {
    gradient: 'from-yellow-500 via-amber-400 to-yellow-500',
    border: 'border-yellow-400/60',
    glow: 'shadow-[0_0_40px_rgba(234,179,8,0.4)]',
    label: 'Gold',
    labelColor: 'text-yellow-400',
    bgAccent: 'bg-yellow-500/10',
  },
};

export function AchievementPopup({ achievement, onClose }) {
  if (!achievement) return null;

  const tier = tierConfig[achievement.tier] || tierConfig.bronze;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        data-testid="achievement-popup-overlay"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

        {/* Popup */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0, y: 40 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 22 }}
          className={`relative w-full max-w-sm rounded-2xl border-2 ${tier.border} ${tier.glow} bg-[hsl(var(--secondary))] overflow-hidden`}
          data-testid="achievement-popup"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-white transition-colors z-10"
            data-testid="achievement-popup-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Shimmer top bar */}
          <div className={`h-1.5 w-full bg-gradient-to-r ${tier.gradient}`} />

          <div className="p-6 pt-5 text-center">
            {/* Logo */}
            <img src={LOGO_URL} alt="JepPrep" className="h-8 mx-auto mb-4 opacity-60" />

            {/* Label */}
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="text-xs font-bold tracking-[0.25em] uppercase text-gray-400 mb-3"
            >
              Achievement Unlocked
            </motion.p>

            {/* Badge icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 250, damping: 15, delay: 0.2 }}
              className={`w-24 h-24 mx-auto rounded-full ${tier.bgAccent} border-2 ${tier.border} flex items-center justify-center mb-4`}
            >
              <span className="text-5xl">{achievement.icon || '🏆'}</span>
            </motion.div>

            {/* Name */}
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="text-xl font-bold text-white mb-1"
              data-testid="achievement-popup-name"
            >
              {achievement.name}
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45 }}
              className="text-gray-400 text-sm mb-4"
              data-testid="achievement-popup-description"
            >
              {achievement.description}
            </motion.p>

            {/* Tier badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${tier.bgAccent} border ${tier.border} mb-5`}
            >
              <Trophy className={`w-3.5 h-3.5 ${tier.labelColor}`} />
              <span className={`text-xs font-semibold ${tier.labelColor}`}>{tier.label}</span>
            </motion.div>

            {/* Continue button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Button
                onClick={onClose}
                className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-5 font-semibold"
                data-testid="achievement-popup-continue"
              >
                Continue
              </Button>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
